import { useEffect, useRef, useState } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

// Download URLs for the footage posters the ingest trigger writes.
//
// A footage clip has no client-side thumbnail — a browser can't decode Brinno's
// MJPEG-in-MP4 — so the server probe extracts a frame to
// `video_posters/{uid}/{pid}/{videoKey}.jpg` and records it on the doc as
// `posterPath`, a gs:// URL. Turning that into something an <img> can load
// costs a signed-URL round trip per clip, so the work is keyed by PATH rather
// than by render: useProjectVideos is a live subscription, and every probe
// field the trigger writes (duration, codec, fps, status) re-emits the whole
// list as a fresh array.
const cacheKey = (video) => `${video.id} ${video.posterPath}`;

export default function usePosterUrls(videos) {
  const [urls, setUrls] = useState({});
  // Every (id, posterPath) we have already asked Storage about — resolved,
  // in flight, or FAILED. A failure is terminal on purpose: the effect re-runs
  // on the next snapshot, and retrying there would loop a broken poster for as
  // long as the tab is open.
  const requestedRef = useRef(new Set());
  // Set on mount rather than initialised true: StrictMode runs effects
  // mount → cleanup → mount, and a flag only ever cleared would leave the hook
  // permanently "unmounted" and drop every resolve on the floor.
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    for (const video of videos || []) {
      if (!video?.posterPath) continue;
      const key = cacheKey(video);
      if (requestedRef.current.has(key)) continue;
      requestedRef.current.add(key);
      // The modular ref() takes a gs:// URL directly; re-deriving a
      // bucket-relative path here would be a second parser to keep in step
      // with the trigger that writes it.
      getDownloadURL(ref(storage, video.posterPath))
        .then((url) => {
          if (mountedRef.current) setUrls((prev) => ({ ...prev, [video.id]: url }));
        })
        .catch(() => {
          // A missing or unreadable poster is cosmetic — the row keeps its
          // placeholder. Swallowed rather than surfaced so one bad file can't
          // reject its way into an unhandled rejection.
        });
    }
  }, [videos]);

  return urls;
}
